import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  ButtonBase,
  Stack,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Snackbar,
  TablePagination,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { tokens } from '../../theme';
import { salesApi, Sale, CreateSaleInput } from '../../services/salesService';
import { serviceApi, Service } from '../../services/serviceService';
import { inventoryApiService, Product } from '../../services/inventoryService';
import { apiClient } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useSalonContextStore } from '../../store/salonContextStore';
import { salonApi, enabledPaymentMethods } from '../../services/salonService';
import {
  PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone, useConfirm,
} from '../../components/ui';
import { formatMoney, formatDateTime, formatDuration, countLabel } from '../../utils/format';

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Gotówka', icon: <PaymentsOutlinedIcon /> },
  { value: 'CARD', label: 'Karta', icon: <CreditCardOutlinedIcon /> },
  { value: 'BLIK', label: 'BLIK', icon: <PhoneIphoneOutlinedIcon /> },
  { value: 'TRANSFER', label: 'Przelew', icon: <AccountBalanceOutlinedIcon /> },
  { value: 'BARTER', label: 'Barter', icon: <SwapHorizIcon /> },
  { value: 'VOUCHER', label: 'Bon', icon: <CardGiftcardOutlinedIcon /> },
];

const METHOD_LABELS: Record<string, string> = {
  CASH: 'Gotówka',
  CARD: 'Karta',
  BLIK: 'BLIK',
  TRANSFER: 'Przelew',
  DEPOSIT: 'Depozyt',
  BARTER: 'Barter',
  VOUCHER: 'Bon',
};

const METHOD_ICONS: Record<string, React.ReactNode> = {
  ...Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.icon])),
  DEPOSIT: <SavingsOutlinedIcon />,
};

const STATUS_LABELS: Record<string, { label: string; tone: PillTone }> = {
  COMPLETED: { label: 'Opłacone', tone: 'success' },
  REFUNDED: { label: 'Zwrócone', tone: 'danger' },
  VOIDED: { label: 'Anulowane', tone: 'warning' },
};

interface CartItem {
  id: string;
  serviceId?: string;
  productId?: string;
  name: string;
  // Podpis pod nazwa: czas trwania dla uslugi, jednostka dla produktu.
  meta: string;
  price: number;
  qty: number;
}

interface StaffOption {
  id: string;
  firstName: string;
  lastName: string;
  serviceIds: string[];
}

interface ClientOption {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

const money2 = (v: unknown) => formatMoney(v, { decimals: 2 });

const fieldLabelSx = { fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 1 } as const;

/** Klikalny kafelek pozycji do dodania do koszyka. */
const PickTile: React.FC<{ name: string; meta: string; price: number; inCart: number; onClick: () => void }> = ({
  name, meta, price, inCart, onClick,
}) => (
  <ButtonBase
    onClick={onClick}
    aria-label={`Dodaj do koszyka: ${name}, ${money2(price)}`}
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      textAlign: 'left',
      p: 1.75,
      borderRadius: '12px',
      border: `1px solid ${inCart ? alpha(tokens.plum, 0.45) : tokens.line}`,
      bgcolor: inCart ? tokens.plumSofter : tokens.paper,
      transition: 'border-color .15s ease, background-color .15s ease, box-shadow .15s ease',
      '&:hover': { borderColor: tokens.plum, bgcolor: tokens.plumSofter, boxShadow: 3 },
      '&:hover .pick-add': { bgcolor: tokens.plum, color: tokens.paper },
    }}
  >
    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 650, lineHeight: 1.35, wordBreak: 'break-word' }}>{name}</Typography>
    <Typography variant="caption" component="div" sx={{ mt: 0.25 }}>{meta}</Typography>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mt: 'auto', pt: 1.25 }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 750, fontVariantNumeric: 'tabular-nums', color: tokens.ink }}>
        {money2(price)}
      </Typography>
      {inCart > 0 ? (
        <Box className="pick-add" sx={{ px: 1, height: 28, minWidth: 28, borderRadius: 999, bgcolor: tokens.plumSoft, color: tokens.plum, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', transition: 'background-color .15s ease, color .15s ease' }}>
          × {inCart}
        </Box>
      ) : (
        <Box className="pick-add" sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', transition: 'background-color .15s ease, color .15s ease' }}>
          <AddIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
    </Stack>
  </ButtonBase>
);

const tileGridSx = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
} as const;

export const SalonSalesPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const user = useAuthStore((s) => s.user);
  const confirm = useConfirm();

  // Shared state
  const [salonId, setSalonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // New sale state
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [pickerTab, setPickerTab] = useState(0);
  // Metody wlaczone w salonie (uwaga klienta 17); domyslnie wszystkie.
  const [activePaymentMethods, setActivePaymentMethods] = useState(PAYMENT_METHODS);
  const [staffList, setStaffList] = useState<StaffOption[]>([]);
  const [clientList, setClientList] = useState<ClientOption[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<StaffOption | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Transactions state
  const [sales, setSales] = useState<Sale[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesPage, setSalesPage] = useState(0);
  const [salesTotal, setSalesTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { activeSalonId, loadSalons } = useSalonContextStore();

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  // Load salon, services, staff, clients
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!activeSalonId) {
        setError('Nie wybrano salonu');
        return;
      }
      const sid = activeSalonId;
      setSalonId(sid);

      const [servicesRes, staffRes, clientsRes, productsRes] = await Promise.all([
        serviceApi.listBySalon(sid),
        apiClient.get(`/staff/salon/${sid}`),
        apiClient.get(`/clients/salon/${sid}`, { params: { limit: 100 } }),
        // Produktow moze nie byc wcale (salon bez magazynu) i to nie jest blad —
        // reszta kasy ma dzialac tak samo.
        inventoryApiService.getProducts({ salonId: sid, includeBrand: true }).catch(() => [] as Product[]),
      ]);

      salonApi
        .getById(sid)
        .then((salon) => {
          const enabled = enabledPaymentMethods(salon);
          setActivePaymentMethods(PAYMENT_METHODS.filter((m) => enabled.includes(m.value)));
        })
        .catch(() => setActivePaymentMethods(PAYMENT_METHODS));

      setServices(servicesRes.filter((s) => s.isActive));
      // Do sprzedazy nadaje sie produkt aktywny i wyceniony. Bez ceny nie ma
      // czego dodac do paragonu.
      setProducts(
        productsRes.filter((p) => p.isActive && Number(p.sellingPrice ?? 0) > 0),
      );
      const staffData = (staffRes.data.data || staffRes.data || [])
        .filter((s: any) => s.isActive)
        .map((s: any) => ({
          id: s.id,
          firstName: s.firstName,
          lastName: s.lastName,
          serviceIds: (s.services || []).map((ss: any) => ss.service?.id || ss.serviceId).filter(Boolean),
        }));
      setStaffList(staffData);
      // Preselect the first staff member via a functional update so this loader
      // does NOT depend on `selectedStaff`. Depending on it recreated the
      // callback on every staff change, re-firing the reload effect →
      // setLoading(true/false) loop = the "flicker" when adding a service.
      setSelectedStaff((prev) => prev ?? staffData[0] ?? null);

      const clientData = (clientsRes.data.data || clientsRes.data || [])
        .map((c: any) => ({ id: c.id, firstName: c.firstName, lastName: c.lastName, phone: c.phone }));
      setClientList(clientData);
    } catch {
      setError('Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  }, [activeSalonId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load transactions
  const loadSales = useCallback(async () => {
    if (!salonId) return;
    setSalesLoading(true);
    try {
      const res = await salesApi.list({ salonId, page: salesPage + 1, limit: 20 });
      setSales(res.data);
      setSalesTotal(res.pagination.total);
    } catch {
      // silent
    } finally {
      setSalesLoading(false);
    }
  }, [salonId, salesPage]);

  useEffect(() => {
    if (tab === 1) loadSales();
  }, [tab, loadSales]);

  // Cart operations — klucz pozycji ma przedrostek, zeby usluga i produkt
  // o tym samym identyfikatorze nigdy sie nie zlaly.
  const addToCart = (service: Service) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.serviceId === service.id);
      if (existing) return prev.map((i) => (i.serviceId === service.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, {
        id: `s:${service.id}`,
        serviceId: service.id,
        name: service.name,
        meta: formatDuration(service.duration),
        price: Number(service.price),
        qty: 1,
      }];
    });
  };

  const addProductToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.productId === product.id);
      if (existing) return prev.map((i) => (i.productId === product.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, {
        id: `p:${product.id}`,
        productId: product.id,
        name: product.name,
        meta: product.unit,
        price: Number(product.sellingPrice ?? 0),
        qty: 1,
      }];
    });
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  };

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.qty, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

  // Submit sale
  const handlePayment = async (method: string) => {
    if (!salonId || !selectedStaff || !selectedClient || cart.length === 0) return;
    setSubmitting(true);
    try {
      const input: CreateSaleInput = {
        salonId,
        staffId: selectedStaff.id,
        clientId: selectedClient.id,
        paymentMethod: method,
        items: cart.map((item) => ({
          serviceId: item.serviceId || null,
          productId: item.productId || null,
          name: item.name,
          quantity: item.qty,
          unitPrice: item.price,
        })),
      };
      await salesApi.create(input);
      setSnackbar({ open: true, message: `Zarejestrowano sprzedaż na ${money2(cartTotal)}`, severity: 'success' });
      setCart([]);
      setSelectedClient(null);
      setPaymentDialogOpen(false);
      loadSales();
    } catch {
      setSnackbar({ open: true, message: 'Nie udało się zarejestrować sprzedaży. Sprawdź połączenie i spróbuj ponownie.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Refund sale
  const handleRefund = async (saleId: string) => {
    try {
      await salesApi.refund(saleId);
      setSnackbar({ open: true, message: 'Zwrot zarejestrowany', severity: 'success' });
      loadSales();
    } catch {
      setSnackbar({ open: true, message: 'Nie udało się zarejestrować zwrotu. Spróbuj ponownie.', severity: 'error' });
    }
  };

  const askRefund = async (sale: Sale) => {
    const ok = await confirm({
      title: 'Zarejestrować zwrot?',
      details: (
        <>
          <strong>{money2(sale.totalAmount)}</strong>
          {sale.client ? ` · ${sale.client.firstName} ${sale.client.lastName}` : ''}
          {` · ${formatDateTime(sale.createdAt)}`}
        </>
      ),
      description: 'Transakcja zostanie oznaczona jako zwrócona i przestanie liczyć się do przychodu.',
      confirmLabel: 'Zarejestruj zwrot',
      tone: 'danger',
    });
    if (ok) handleRefund(sale.id);
  };

  const filteredClients = clientList.filter((c) => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    return `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) || (c.phone || '').includes(q);
  });

  const filteredSales = sales.filter((s) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const clientName = s.client ? `${s.client.firstName} ${s.client.lastName}`.toLowerCase() : '';
    const itemNames = s.items.map((i) => i.name.toLowerCase()).join(' ');
    return clientName.includes(q) || itemNames.includes(q) || s.id.toLowerCase().includes(q);
  });

  const qtyInCart = (key: string) => cart.find((i) => i.id === key)?.qty ?? 0;
  const missingHint = !selectedClient && !selectedStaff
    ? 'Wybierz klienta i pracownika, aby przejść do płatności'
    : !selectedClient
      ? 'Wybierz klienta, aby przejść do płatności'
      : 'Wybierz pracownika, aby przejść do płatności';

  const header = (
    <PageHeader
      title="Sprzedaż"
      description={tab === 0 ? 'Dodaj usługi i produkty do rachunku, wybierz klienta i przyjmij płatność.' : 'Historia sprzedaży w kasie salonu i zwroty.'}
    >
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${tokens.line}` }}>
        <Tab label="Nowa sprzedaż" />
        <Tab label="Transakcje" />
      </Tabs>
    </PageHeader>
  );

  if (loading) return <Box>{header}<LoadingState variant="page" count={3} rows={4} /></Box>;
  if (error) return <Box>{header}<Alert severity="error">{error === 'Nie wybrano salonu' ? 'Nie wybrano salonu. Wybierz salon w przełączniku u góry strony.' : 'Nie udało się załadować danych kasy. Odśwież stronę, a jeśli problem wróci — sprawdź połączenie.'}</Alert></Box>;

  return (
    <Box>
      {header}

      {/* NOWA SPRZEDAZ */}
      {tab === 0 && (
        <Grid container spacing={2.5} alignItems="flex-start">
          {/* Wybor uslug i produktow */}
          <Grid item xs={12} md={7}>
            <SectionCard padding="none">
              <Tabs
                value={pickerTab}
                onChange={(_, v) => setPickerTab(v)}
                sx={{ px: { xs: 1, md: 1.5 }, borderBottom: `1px solid ${tokens.line}` }}
              >
                <Tab label={`Usługi (${services.length})`} icon={<ContentCutOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                <Tab label={`Produkty (${products.length})`} icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              </Tabs>

              <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                {pickerTab === 0 && (
                  services.length === 0 ? (
                    <EmptyState
                      compact
                      icon={<ContentCutOutlinedIcon />}
                      title="Brak aktywnych usług"
                      description="Usługi dodasz i włączysz w cenniku salonu (Usługi i magazyn → Usługi i cennik)."
                    />
                  ) : (
                    <Box sx={tileGridSx}>
                      {services.map((service) => (
                        <PickTile
                          key={service.id}
                          name={service.name}
                          meta={formatDuration(service.duration)}
                          price={Number(service.price)}
                          inCart={qtyInCart(`s:${service.id}`)}
                          onClick={() => addToCart(service)}
                        />
                      ))}
                    </Box>
                  )
                )}

                {pickerTab === 1 && (
                  products.length === 0 ? (
                    <EmptyState
                      compact
                      icon={<Inventory2OutlinedIcon />}
                      title="Brak produktów do sprzedaży"
                      description="Na liście pojawiają się produkty aktywne z ustawioną ceną sprzedaży."
                    />
                  ) : (
                    <Box sx={tileGridSx}>
                      {products.map((product) => (
                        <PickTile
                          key={product.id}
                          name={product.name}
                          meta={product.sku ? `${product.sku} · ${product.unit}` : product.unit}
                          price={Number(product.sellingPrice ?? 0)}
                          inCart={qtyInCart(`p:${product.id}`)}
                          onClick={() => addProductToCart(product)}
                        />
                      ))}
                    </Box>
                  )
                )}
              </Box>
            </SectionCard>
          </Grid>

          {/* Koszyk */}
          <Grid item xs={12} md={5} sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            <SectionCard
              title="Rachunek"
              subtitle={cartCount ? countLabel(cartCount, 'pozycja', 'pozycje', 'pozycji') : 'Brak pozycji'}
              padding="none"
              action={(
                <Button
                  size="small"
                  onClick={() => { setCart([]); setSelectedClient(null); }}
                  disabled={cart.length === 0}
                  sx={{ color: tokens.muted }}
                >
                  Wyczyść
                </Button>
              )}
            >
              {/* Pracownik — kto rejestruje sprzedaz */}
              {staffList.length > 0 && (
                <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2 }}>
                  <Typography sx={fieldLabelSx}>Pracownik</Typography>
                  {staffList.length > 1 ? (
                    <Stack direction="row" gap={0.75} flexWrap="wrap" role="radiogroup" aria-label="Pracownik">
                      {staffList.map((s) => {
                        const selected = selectedStaff?.id === s.id;
                        return (
                          <Chip
                            key={s.id}
                            role="radio"
                            aria-checked={selected}
                            label={`${s.firstName} ${s.lastName}`}
                            onClick={() => setSelectedStaff(s)}
                            variant={selected ? 'filled' : 'outlined'}
                            color={selected ? 'primary' : 'default'}
                            icon={selected ? <CheckIcon /> : undefined}
                            sx={{
                              height: 36,
                              fontSize: '0.875rem',
                              ...(selected ? { border: `1px solid ${alpha(tokens.plum, 0.35)}` } : {}),
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <PersonAvatar firstName={staffList[0].firstName} lastName={staffList[0].lastName} seed={staffList[0].id} size={28} />
                      <Typography sx={{ fontWeight: 600 }}>{staffList[0].firstName} {staffList[0].lastName}</Typography>
                    </Stack>
                  )}
                </Box>
              )}

              {/* Klient */}
              <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 2 }}>
                <Typography sx={fieldLabelSx}>Klient</Typography>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setClientDialogOpen(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setClientDialogOpen(true); } }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.25,
                    pr: 0.75,
                    borderRadius: '12px',
                    border: `1px ${selectedClient ? 'solid' : 'dashed'} ${selectedClient ? tokens.line : tokens.lineStrong}`,
                    bgcolor: selectedClient ? tokens.paper : tokens.surface,
                    cursor: 'pointer',
                    transition: 'border-color .15s ease, background-color .15s ease',
                    '&:hover': { borderColor: tokens.plum, bgcolor: tokens.plumSofter },
                    '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
                  }}
                >
                  {selectedClient ? (
                    <PersonAvatar firstName={selectedClient.firstName} lastName={selectedClient.lastName} seed={selectedClient.id} size={40} />
                  ) : (
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      <PersonAddAltIcon sx={{ fontSize: 20 }} />
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: selectedClient ? 650 : 600, color: selectedClient ? tokens.ink : tokens.plum }} noWrap>
                      {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'Wybierz klienta'}
                    </Typography>
                    <Typography variant="caption" component="div" noWrap>
                      {selectedClient ? (selectedClient.phone || 'Kliknij, aby zmienić') : 'Wymagany do sprzedaży'}
                    </Typography>
                  </Box>
                  {selectedClient ? (
                    <IconButton
                      size="small"
                      aria-label="Usuń klienta z rachunku"
                      onClick={(e) => { e.stopPropagation(); setSelectedClient(null); }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : (
                    <AddIcon sx={{ color: tokens.plum, fontSize: 20, mr: 0.75 }} />
                  )}
                </Box>
              </Box>

              {/* Pozycje */}
              <Box sx={{ borderTop: `1px solid ${tokens.line}` }}>
                {cart.length === 0 ? (
                  <EmptyState
                    compact
                    icon={<ReceiptLongOutlinedIcon />}
                    title="Rachunek jest pusty"
                    description="Kliknij usługę lub produkt, aby dodać go do rachunku."
                  />
                ) : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {cart.map((item) => (
                      <Stack
                        component="li"
                        key={item.id}
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{ pl: { xs: 2, md: 2.5 }, pr: { xs: 1, md: 1.5 }, py: 1.25, borderBottom: `1px solid ${tokens.line}`, '&:last-of-type': { borderBottom: 0 } }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{item.name}</Typography>
                          <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {item.meta}{item.qty > 1 ? ` · ${item.qty} × ${money2(item.price)}` : ''}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {money2(item.price * item.qty)}
                        </Typography>
                        <IconButton size="small" aria-label={`Usuń z rachunku: ${item.name}`} onClick={() => removeFromCart(item.id)}>
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Suma i platnosc */}
              <Box sx={{ borderTop: `1px solid ${tokens.lineStrong}`, bgcolor: tokens.surface, px: { xs: 2, md: 2.5 }, py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} sx={{ mb: 1.75 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: tokens.inkSoft }}>Do zapłaty</Typography>
                  <Typography component="div" sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 750, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {money2(cartTotal)}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={cart.length === 0 || !selectedStaff || !selectedClient}
                  startIcon={<CreditCardOutlinedIcon />}
                  onClick={() => setPaymentDialogOpen(true)}
                >
                  Przejdź do płatności
                </Button>
                {cart.length > 0 && (!selectedStaff || !selectedClient) && (
                  <Typography variant="caption" component="div" sx={{ mt: 1, textAlign: 'center' }}>
                    {missingHint}
                  </Typography>
                )}
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {/* TRANSAKCJE */}
      {tab === 1 && (
        <SectionCard
          title="Transakcje"
          subtitle={salesTotal ? countLabel(salesTotal, 'transakcja', 'transakcje', 'transakcji') : undefined}
          padding="none"
        >
          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${tokens.line}` }}>
            <TextField
              placeholder="Szukaj po kliencie lub usłudze"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: { xs: '100%', sm: 320 } }}
              inputProps={{ 'aria-label': 'Szukaj transakcji' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20 }} /></InputAdornment>,
              }}
            />
          </Box>
          {salesLoading ? (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}><LoadingState variant="table" rows={5} /></Box>
          ) : filteredSales.length === 0 ? (
            searchQuery ? (
              <EmptyState
                compact
                icon={<SearchIcon />}
                title={`Brak transakcji dla „${searchQuery}”`}
                description="Szukanie obejmuje bieżącą stronę listy. Sprawdź pisownię albo przejdź na inną stronę."
              />
            ) : (
              <EmptyState
                icon={<ReceiptLongOutlinedIcon />}
                title="Nie ma jeszcze transakcji"
                description="Sprzedaż zarejestrowana w zakładce „Nowa sprzedaż” pojawi się tutaj."
                actions={<Button variant="outlined" onClick={() => setTab(0)}>Nowa sprzedaż</Button>}
              />
            )
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 960 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Klient</TableCell>
                      <TableCell>Pozycje</TableCell>
                      <TableCell>Pracownik</TableCell>
                      <TableCell align="right">Kwota</TableCell>
                      <TableCell>Metoda</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" aria-label="Akcje" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSales.map((s) => {
                      const st = STATUS_LABELS[s.status] || { label: s.status, tone: 'neutral' as PillTone };
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(s.createdAt)}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>
                              {s.client ? `${s.client.firstName} ${s.client.lastName}` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Stack direction="row" gap={0.5} flexWrap="wrap">
                              {s.items.map((item) => (
                                <Chip key={item.id} label={`${item.name}${item.quantity > 1 ? ` × ${item.quantity}` : ''}`} size="small" />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={1}>
                              <PersonAvatar firstName={s.staff.firstName} lastName={s.staff.lastName} seed={s.staff.id || s.staffId} size={28} />
                              <Typography sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>{s.staff.firstName} {s.staff.lastName}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {money2(s.totalAmount)}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={0.75} sx={{ color: tokens.inkSoft, '& .MuiSvgIcon-root': { fontSize: 18, color: tokens.muted } }}>
                              {METHOD_ICONS[s.paymentMethod] || <PaymentsOutlinedIcon />}
                              <Typography sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>{METHOD_LABELS[s.paymentMethod] || s.paymentMethod}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell><StatusPill label={st.label} tone={st.tone} size="small" /></TableCell>
                          <TableCell align="right">
                            {s.status === 'COMPLETED' && user?.role === 'SALON_OWNER' && (
                              <Button size="small" color="error" onClick={() => askRefund(s)}>
                                Zwrot
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={salesTotal}
                page={salesPage}
                onPageChange={(_, p) => setSalesPage(p)}
                rowsPerPage={20}
                rowsPerPageOptions={[20]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
                getItemAriaLabel={(type) => (type === 'next' ? 'Następna strona' : type === 'previous' ? 'Poprzednia strona' : type === 'first' ? 'Pierwsza strona' : 'Ostatnia strona')}
              />
            </>
          )}
        </SectionCard>
      )}

      {/* Wybor klienta */}
      <Dialog open={clientDialogOpen} onClose={() => setClientDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Wybierz klienta</DialogTitle>
        <DialogContent>
          <TextField
            placeholder="Imię, nazwisko lub telefon"
            fullWidth
            autoFocus
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Szukaj klienta' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20 }} /></InputAdornment>,
            }}
          />
          <List sx={{ maxHeight: 340, overflow: 'auto', mt: 1.5, mx: -1 }}>
            {filteredClients.map((c) => (
              <ListItemButton
                key={c.id}
                selected={selectedClient?.id === c.id}
                onClick={() => {
                  setSelectedClient(c);
                  setClientDialogOpen(false);
                  setClientSearch('');
                }}
              >
                <ListItemAvatar sx={{ minWidth: 48 }}>
                  <PersonAvatar firstName={c.firstName} lastName={c.lastName} seed={c.id} size={36} />
                </ListItemAvatar>
                <ListItemText
                  primary={`${c.firstName} ${c.lastName}`}
                  secondary={c.phone}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
                {selectedClient?.id === c.id && <CheckIcon sx={{ color: tokens.plum, fontSize: 20 }} />}
              </ListItemButton>
            ))}
            {filteredClients.length === 0 && (
              <EmptyState
                compact
                icon={<SearchIcon />}
                title="Nie znaleziono klientów"
                description="Sprawdź pisownię albo wyszukaj po numerze telefonu."
              />
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setClientDialogOpen(false); setClientSearch(''); }}>
            Anuluj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metoda platnosci */}
      <Dialog open={paymentDialogOpen} onClose={() => !submitting && setPaymentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Jak klient płaci?</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 1.5, mb: 2, borderRadius: '12px', bgcolor: tokens.surface, border: `1px solid ${tokens.line}` }}>
            <Typography variant="caption" component="div">Do zapłaty</Typography>
            <Typography sx={{ fontSize: '2rem', fontWeight: 750, letterSpacing: '-0.02em', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
              {money2(cartTotal)}
            </Typography>
            {(selectedClient || selectedStaff) && (
              <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 0.25, px: 2 }}>
                {[selectedClient && `${selectedClient.firstName} ${selectedClient.lastName}`, selectedStaff && `obsługuje ${selectedStaff.firstName} ${selectedStaff.lastName}`].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            {activePaymentMethods.map((pm) => (
              <ButtonBase
                key={pm.value}
                disabled={submitting}
                onClick={() => handlePayment(pm.value)}
                sx={{
                  flexDirection: 'column',
                  gap: 0.75,
                  py: 2,
                  borderRadius: '12px',
                  border: `1px solid ${tokens.lineStrong}`,
                  bgcolor: tokens.paper,
                  transition: 'border-color .15s ease, background-color .15s ease',
                  '&:hover': { borderColor: tokens.plum, bgcolor: tokens.plumSofter },
                  '&.Mui-disabled': { opacity: 0.55 },
                  '& .MuiSvgIcon-root': { fontSize: 26, color: tokens.plum },
                }}
              >
                {pm.icon}
                <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{pm.label}</Typography>
              </ButtonBase>
            ))}
          </Box>
          {activePaymentMethods.length === 0 && (
            <Alert severity="warning">W ustawieniach salonu nie ma włączonej żadnej metody płatności.</Alert>
          )}
          {submitting && (
            <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: tokens.inkSoft }}>Rejestruję sprzedaż…</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} disabled={submitting}>Anuluj</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
